import { useRef } from 'react';
import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { Button, ModalButtonWithForm } from 'components/common';
import { SortableListInput } from 'components/common/form';

import type { FormApi } from 'informed';
import type { UserCustomAttribute } from 'shared-types';

type FormValues = { keys: string[] };

interface Props {
  attribute: UserCustomAttribute;
  optionGroupIndex: number;
  updateData: () => Promise<void>;
}

const UserAttributesOptionsSort = ({
  attribute,
  optionGroupIndex,
  updateData
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const optionGroup = attribute.optionGroups[optionGroupIndex];

  const labels = {};
  const optionsByKey = {};

  optionGroup.options.forEach((option) => {
    labels[option.key] = option.label;
    optionsByKey[option.key] = option;
  });

  const handleSortAlphabetically = () => {
    const keys = [...formApi.current.getState().values.keys];

    const sortedKeys = keys.sort((a, b) =>
      labels[a].toLowerCase() > labels[b].toLowerCase() ? 1 : -1
    );

    formApi.current.setValue('keys', sortedKeys);
  };

  const handleSubmit = async ({ keys }) => {
    const data = {
      optionGroups: [
        ...attribute.optionGroups.slice(0, optionGroupIndex),
        {
          ...optionGroup,
          options: keys.map((key) => optionsByKey[key])
        },
        ...attribute.optionGroups.slice(optionGroupIndex + 1)
      ]
    };

    await api.put(`/admin/user-custom-attributes/${attribute.id}`, data);

    await updateData();

    dispatch(
      actions.addNotice({
        text: messages.commonChangesSavesSuccessfully,
        type: 'success'
      })
    );
  };

  const initialValues = {
    keys: optionGroup.options.map((option) => option.key)
  };

  return (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonDisabled={!optionGroup.options?.length}
      buttonIcon="arrows-alt-v"
      buttonSize="header"
      buttonText={messages.commonEditOrder}
      contentStyle={{ padding: 0 }}
      footerContent={
        <Button
          category="light"
          icon="sort-alpha-down"
          onClick={handleSortAlphabetically}
          size="medium"
          style={{ float: 'left' }}
          text={messages.commonSortAlphabetically}
        />
      }
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalTitle={messages.commonEditOrder}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
    >
      {() => (
        <SortableListInput itemWithBorder={true} labels={labels} field="keys" />
      )}
    </ModalButtonWithForm>
  );
};

export default UserAttributesOptionsSort;
